import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Add as AddIcon,
    Close as CloseIcon,
    DragIndicator as DragIcon,
    Remove as RemoveIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
} from "@mui/icons-material";
import {
    Badge,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItemButton,
    ListItemText,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ClipLoader from "react-spinners/ClipLoader";

import TextArea from "components/atoms/TextArea";

import "./CreateDynamicValueModal.css";

interface FunctionConfig {
    fn_name: string;
    fn_values?: { [key: string]: any };
}

interface SourceField {
    value: string;
    label: string;
    prefix?: string;
    suffix?: string;
    defaultValue?: string;
    fn?: FunctionConfig[];
}

interface DynamicValueModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** May return false (or a promise resolving to false) to keep the modal open, e.g. when saving fails. */
    onSubmit: (data: {
        name: string;
        fields?: SourceField[];
        payload?: string;
        mode: "basic" | "advanced";
        index: number;
    }) => void | boolean | Promise<void | boolean>;
    availableSourceFields?: SourceField[];
    allFunctions?: Array<{
        id: string;
        value: string;
        label: string;
        value_types?: string;
    }>;
    editedField?: {
        name: string;
        fields?: SourceField[];
        payload?: string;
        mode?: "basic" | "advanced";
    };
    editFieldIndex?: number;
    notAvailableFieldNames?: string[];
    /** Disables editing of the variable name (server-side dynamic field names are immutable). */
    nameReadOnly?: boolean;
}

// Sortable Field Item Component
interface SortableFieldItemProps {
    field: SourceField;
    onSettings: (field: SourceField) => void;
    onRemove: (fieldId: string) => void;
}

const SortableFieldItem: React.FC<SortableFieldItemProps> = ({
    field,
    onSettings,
    onRemove,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: field.value });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <Box
            ref={setNodeRef}
            style={style}
            sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor: isDragging ? "#fff3cd" : "#fff3cd",
                border: isDragging ? "2px dashed #fff3cd" : "1px solid #fff3cd",
                mb: 1,
                cursor: isDragging ? "grabbing" : "default",
            }}
        >
            <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                    {...attributes}
                    {...listeners}
                    sx={{
                        cursor: "grab",
                        display: "flex",
                        alignItems: "center",
                        "&:active": {
                            cursor: "grabbing",
                        },
                    }}
                >
                    <DragIcon color="action" sx={{ cursor: "grab" }} />
                </Box>
                <Typography variant="body2" sx={{ flex: 1, fontWeight: 500 }}>
                    {field.label}
                </Typography>
                <IconButton
                    size="small"
                    onClick={() => onSettings(field)}
                    sx={{
                        color: "action.active",
                        "&:hover": { backgroundColor: "action.hover" },
                    }}
                >
                    <SettingsIcon fontSize="small" />
                </IconButton>
                <IconButton
                    size="small"
                    onClick={() => onRemove(field.value)}
                    sx={{
                        color: "error.main",
                        "&:hover": {
                            color: "error.dark",
                        },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Stack>
        </Box>
    );
};

// Function Configuration Item Component
interface FunctionConfigItemProps {
    func: FunctionConfig;
    onUpdate: (func: FunctionConfig) => void;
    onRemove: () => void;
    allFunctions: Array<{
        id: string;
        value: string;
        label: string;
        value_types?: string;
    }>;
}

const FunctionConfigItem: React.FC<FunctionConfigItemProps> = ({
    func,
    onUpdate,
    onRemove,
    allFunctions,
}) => {
    const handleFunctionNameChange = (fn_name: string) => {
        onUpdate({ ...func, fn_name, fn_values: {} });
    };

    const handleFnValuesChange = (key: string, value: any) => {
        onUpdate({
            ...func,
            fn_values: {
                ...func.fn_values,
                [key]: value,
            },
        });
    };

    const renderParameterInputs = () => {
        switch (func.fn_name) {
            case "replace":
                return (
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="Search"
                            size="small"
                            value={func.fn_values?.search || ""}
                            onChange={(e) =>
                                handleFnValuesChange("search", e.target.value)
                            }
                            placeholder="Text to replace"
                        />
                        <TextField
                            label="Replace"
                            size="small"
                            value={func.fn_values?.replace || ""}
                            onChange={(e) =>
                                handleFnValuesChange("replace", e.target.value)
                            }
                            placeholder="Replacement text"
                        />
                    </Stack>
                );
            case "substring":
                return (
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="Start"
                            type="number"
                            size="small"
                            value={func.fn_values?.start || ""}
                            onChange={(e) =>
                                handleFnValuesChange(
                                    "start",
                                    parseInt(e.target.value) || 0,
                                )
                            }
                            placeholder="Start index"
                        />
                        <TextField
                            label="End"
                            type="number"
                            size="small"
                            value={func.fn_values?.end || ""}
                            onChange={(e) =>
                                handleFnValuesChange(
                                    "end",
                                    parseInt(e.target.value) || undefined,
                                )
                            }
                            placeholder="End index (optional)"
                        />
                    </Stack>
                );
            case "pad_with":
                return (
                    <Stack direction="row" spacing={1}>
                        <TextField
                            label="Length"
                            type="number"
                            size="small"
                            value={func.fn_values?.length || ""}
                            onChange={(e) =>
                                handleFnValuesChange(
                                    "length",
                                    parseInt(e.target.value) || 0,
                                )
                            }
                            placeholder="Target length"
                        />
                        <TextField
                            label="Character"
                            size="small"
                            value={func.fn_values?.character || " "}
                            onChange={(e) =>
                                handleFnValuesChange(
                                    "character",
                                    e.target.value,
                                )
                            }
                            placeholder="Padding character"
                        />
                    </Stack>
                );
            case "max_len":
                return (
                    <TextField
                        label="Max Length"
                        type="number"
                        size="small"
                        value={func.fn_values?.length || ""}
                        onChange={(e) =>
                            handleFnValuesChange(
                                "length",
                                parseInt(e.target.value) || 0,
                            )
                        }
                        placeholder="Maximum character length"
                        helperText="Text will be truncated if longer than this length"
                    />
                );

            default:
                // For any unknown functions, show a generic parameter input
                return <></>;
        }
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack spacing={2}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Function</InputLabel>
                        <Select
                            value={func.fn_name}
                            onChange={(e) =>
                                handleFunctionNameChange(e.target.value)
                            }
                            label="Function"
                        >
                            {allFunctions.map((functionItem) => (
                                <MenuItem
                                    key={functionItem.id}
                                    value={functionItem.value}
                                >
                                    {functionItem.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton
                        size="small"
                        onClick={onRemove}
                        sx={{ color: "error.main" }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Stack>
                {renderParameterInputs()}
            </Stack>
        </Paper>
    );
};

interface ValidationErrors {
    name?: string;
    fields?: string;
    payload?: string[];
}

/**
 * Checks every `((fn(params)))` call in the payload against the parameter types
 * the function declares, returning one message per problem.
 */
const collectPayloadIssues = (
    payload: string,
    allFunctions: Array<{ value: string; label: string; value_types?: string }>,
): string[] => {
    const fnRegex = /\(\(\s*(\w+)\(\s*(.*?)\s*\)\s*\)\)/g;
    const issues: string[] = [];
    let fnMatch;

    while ((fnMatch = fnRegex.exec(payload)) !== null) {
        const fnName = fnMatch[1];
        const paramsStr = fnMatch[2];
        const fn = allFunctions.find((f) => f.value === fnName);
        if (!fn?.value_types) continue;

        try {
            const expectedTypes = JSON.parse(fn.value_types) as Record<
                string,
                string
            >;
            if (Object.keys(expectedTypes).length === 0) continue;

            const providedParams: Record<string, string> = {};
            if (paramsStr.trim()) {
                paramsStr.split(",").forEach((p: string) => {
                    const colonIdx = p.indexOf(":");
                    if (colonIdx !== -1) {
                        providedParams[p.substring(0, colonIdx).trim()] = p
                            .substring(colonIdx + 1)
                            .trim();
                    }
                });
            }

            for (const [key, type] of Object.entries(expectedTypes)) {
                if (!providedParams[key] || providedParams[key] === "") {
                    issues.push(`${fn.label}: '${key}' parameter is required`);
                } else if (
                    type === "int" &&
                    !/^\d+$/.test(providedParams[key])
                ) {
                    issues.push(`${fn.label}: '${key}' must be a number`);
                }
            }
        } catch {}
    }

    return issues;
};

const AdvancedPayloadInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
    availableSourceFields: SourceField[];
    allFunctions: Array<{
        id: string;
        value: string;
        label: string;
        value_types?: string;
    }>;
    error?: boolean;
    helperText?: React.ReactNode;
}> = ({
    value,
    onChange,
    availableSourceFields,
    allFunctions,
    error = false,
    helperText,
}) => {
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [dropdownType, setDropdownType] = useState<
        "function" | "source" | null
    >(null);
    const [filterText, setFilterText] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState(0);
    const [triggerStart, setTriggerStart] = useState(0);

    const filteredItems = useMemo(() => {
        if (dropdownType === "function") {
            return allFunctions.filter((f) =>
                f.label.toLowerCase().includes(filterText.toLowerCase()),
            );
        }
        if (dropdownType === "source") {
            return availableSourceFields
                .filter((f) => f.value !== "-")
                .filter((f) =>
                    f.label.toLowerCase().includes(filterText.toLowerCase()),
                );
        }
        return [];
    }, [dropdownType, filterText, allFunctions, availableSourceFields]);

    const isOpen = dropdownType !== null && filteredItems.length > 0;

    useEffect(() => {
        if (isOpen && listRef.current) {
            const el = listRef.current.children[
                highlightedIndex
            ] as HTMLElement;
            el?.scrollIntoView({ block: "nearest" });
        }
    }, [highlightedIndex, isOpen]);

    const closeDropdown = useCallback(() => {
        setDropdownType(null);
        setFilterText("");
        setHighlightedIndex(0);
    }, []);

    const detectTrigger = useCallback(
        (text: string, cursorPos: number) => {
            const textBeforeCursor = text.substring(0, cursorPos);

            let activeTrigger: {
                type: "function" | "source";
                pos: number;
                filter: string;
            } | null = null;

            const lastFnOpen = textBeforeCursor.lastIndexOf("((");
            if (lastFnOpen !== -1) {
                const afterTrigger = textBeforeCursor.substring(lastFnOpen + 2);
                if (!afterTrigger.includes("))")) {
                    activeTrigger = {
                        type: "function",
                        pos: lastFnOpen,
                        filter: afterTrigger.trim(),
                    };
                }
            }

            const lastSrcOpen = textBeforeCursor.lastIndexOf("{{");
            if (lastSrcOpen !== -1) {
                const afterTrigger = textBeforeCursor.substring(
                    lastSrcOpen + 2,
                );
                if (!afterTrigger.includes("}}")) {
                    if (!activeTrigger || lastSrcOpen > activeTrigger.pos) {
                        activeTrigger = {
                            type: "source",
                            pos: lastSrcOpen,
                            filter: afterTrigger.trim(),
                        };
                    }
                }
            }

            if (activeTrigger) {
                setDropdownType(activeTrigger.type);
                setFilterText(activeTrigger.filter);
                setTriggerStart(activeTrigger.pos);
                setHighlightedIndex(0);
            } else {
                closeDropdown();
            }
        },
        [closeDropdown],
    );

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = e.target.value;
        const cursor = e.target.selectionStart || 0;
        onChange(newValue);
        detectTrigger(newValue, cursor);
    };

    const getFunctionPreview = useCallback(
        (item: { value: string; value_types?: string }) => {
            if (item.value_types) {
                try {
                    const types = JSON.parse(item.value_types) as Record<
                        string,
                        string
                    >;
                    const params = Object.entries(types)
                        .map(([k, t]) => `${k}:${t}`)
                        .join(", ");
                    if (params) return `((${item.value}(${params})))`;
                } catch {}
            }
            return `((${item.value}()))`;
        },
        [],
    );

    const handleSelect = useCallback(
        (item: { value: string; label: string; value_types?: string }) => {
            const textarea = inputRef.current;
            if (!textarea) return;

            const cursor = textarea.selectionStart || 0;
            const before = value.substring(0, triggerStart);
            const after = value.substring(cursor);

            let insertion: string;
            let cursorOffset: number;

            if (dropdownType === "function") {
                let paramTemplate = "";
                let firstParamKeyLength = 0;

                if (item.value_types) {
                    try {
                        const valueTypes = JSON.parse(
                            item.value_types,
                        ) as Record<string, string>;
                        const paramKeys = Object.keys(valueTypes);
                        if (paramKeys.length > 0) {
                            paramTemplate = paramKeys
                                .map((k) => `${k}:`)
                                .join(",");
                            firstParamKeyLength = paramKeys[0].length;
                        }
                    } catch {}
                }

                insertion = `((${item.value}(${paramTemplate})))`;

                if (paramTemplate) {
                    cursorOffset =
                        2 + item.value.length + 1 + firstParamKeyLength + 1;
                } else {
                    cursorOffset = insertion.length;
                }
            } else {
                insertion = `{{${item.value}}}`;
                cursorOffset = insertion.length;
            }

            const newValue = before + insertion + after;
            onChange(newValue);
            closeDropdown();

            requestAnimationFrame(() => {
                const newPos = before.length + cursorOffset;
                textarea.setSelectionRange(newPos, newPos);
                textarea.focus();
            });
        },
        [value, triggerStart, dropdownType, onChange, closeDropdown],
    );

    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (!isOpen) return;

        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                setHighlightedIndex((prev) =>
                    Math.min(prev + 1, filteredItems.length - 1),
                );
                break;
            case "ArrowUp":
                e.preventDefault();
                setHighlightedIndex((prev) => Math.max(prev - 1, 0));
                break;
            case "Enter":
                e.preventDefault();
                e.stopPropagation();
                if (filteredItems[highlightedIndex]) {
                    handleSelect(filteredItems[highlightedIndex]);
                }
                break;
            case "Escape":
                e.preventDefault();
                e.stopPropagation();
                closeDropdown();
                break;
        }
    };

    const handleBlur = useCallback(
        (e: React.FocusEvent) => {
            const related = e.relatedTarget as Node | null;
            if (
                related &&
                (e.currentTarget as Node)?.parentElement?.contains(related)
            ) {
                return;
            }
            closeDropdown();
        },
        [closeDropdown],
    );

    return (
        <Box>
            <TextArea
                minRows={4}
                maxRows={10}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
                ref={inputRef}
                placeholder="Use (( for functions, {{ for source fields. Example: My name is ((upper_case())){{first_name}}"
                style={{ width: "100%" }}
                error={error}
            />
            {helperText ? (
                <FormHelperText error={error}>{helperText}</FormHelperText>
            ) : null}
            {isOpen && (
                <Paper
                    elevation={4}
                    onMouseDown={(e) => e.preventDefault()}
                    sx={{
                        maxHeight: 200,
                        overflow: "auto",
                        mt: 0.5,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                    }}
                >
                    <Box
                        sx={{
                            px: 1.5,
                            py: 0.75,
                            backgroundColor:
                                dropdownType === "function"
                                    ? "#e3f2fd"
                                    : "#fff3e0",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                            position: "sticky",
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 700,
                                color:
                                    dropdownType === "function"
                                        ? "#1565c0"
                                        : "#e65100",
                            }}
                        >
                            {dropdownType === "function"
                                ? "Functions"
                                : "Source Fields"}
                        </Typography>
                    </Box>
                    <List dense disablePadding ref={listRef}>
                        {filteredItems.map((item, index) => (
                            <ListItemButton
                                key={item.value}
                                selected={index === highlightedIndex}
                                onClick={() => handleSelect(item)}
                                sx={{
                                    py: 0.5,
                                    "&.Mui-selected": {
                                        backgroundColor:
                                            dropdownType === "function"
                                                ? "rgba(21, 101, 192, 0.08)"
                                                : "rgba(230, 81, 0, 0.08)",
                                    },
                                    "&:hover": {
                                        backgroundColor:
                                            dropdownType === "function"
                                                ? "rgba(21, 101, 192, 0.04)"
                                                : "rgba(230, 81, 0, 0.04)",
                                    },
                                }}
                            >
                                <ListItemText
                                    primary={item.label}
                                    secondary={
                                        dropdownType === "function"
                                            ? getFunctionPreview(item)
                                            : `{{${item.value}}}`
                                    }
                                    primaryTypographyProps={{
                                        variant: "body2",
                                        fontWeight: 500,
                                    }}
                                    secondaryTypographyProps={{
                                        variant: "caption",
                                        fontFamily: "monospace",
                                        color: "text.secondary",
                                    }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Paper>
            )}
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
            >
                {"Use for functions: "}
                <code
                    style={{
                        backgroundColor: "#f5f5f5",
                        padding: "0 4px",
                        borderRadius: 2,
                        fontFamily: "monospace",
                    }}
                >
                    {"((function()))"}
                </code>
                {" and for source fields: "}

                <code
                    style={{
                        backgroundColor: "#f5f5f5",
                        padding: "0 4px",
                        borderRadius: 2,
                        fontFamily: "monospace",
                    }}
                >
                    {"{{field_name}}"}
                </code>
            </Typography>
        </Box>
    );
};

const CreateDynamicValueModal: React.FC<DynamicValueModalProps> = ({
    isOpen = true,
    onClose,
    onSubmit,
    availableSourceFields,
    allFunctions = [],
    editedField,
    editFieldIndex = null,
    notAvailableFieldNames = [],
    nameReadOnly = false,
}) => {
    const localAvailableSourceFields = useMemo(() => {
        return availableSourceFields?.filter(
            (item, index) => item?.value !== "-",
        );
    }, [availableSourceFields]);

    const [variableName, setVariableName] = useState("");
    const [selectedFields, setSelectedFields] = useState<SourceField[]>([]);
    const [preview, setPreview] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showAvailableFields, setShowAvailableFields] = useState(true);
    const [mode, setMode] = useState<"basic" | "advanced">("basic");
    const [advancedPayload, setAdvancedPayload] = useState("");
    // Validation surfaces inline on the offending control (helper text + danger
    // border) rather than in a modal alert, so the user can see what to fix
    // while they fix it.
    const [errors, setErrors] = useState<ValidationErrors>({});

    useEffect(() => {
        setErrors({});
        if (editedField) {
            setVariableName(editedField.name);
            if (editedField.mode === "advanced") {
                setMode("advanced");
                setAdvancedPayload(editedField.payload || "");
                setSelectedFields([]);
            } else {
                setMode("basic");
                setSelectedFields(editedField.fields || []);
                setAdvancedPayload("");
            }
        }
    }, [editedField]);
    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    useEffect(() => {
        if (selectedFields.length === 0) {
            setPreview("");
            return;
        }

        let result = "";
        selectedFields.forEach((field, index) => {
            let fieldValue = field.defaultValue || field.value;

            result += (field.prefix || "") + fieldValue + (field.suffix || "");
        });

        setPreview(result);
    }, [selectedFields]);

    const handleAddField = (field: SourceField) => {
        setErrors((prev) => ({ ...prev, fields: undefined }));
        setSelectedFields([
            ...selectedFields,
            {
                ...field,
                prefix: "",
                suffix: "",
                defaultValue: "",
                fn: [],
            },
        ]);
    };

    const handleRemoveField = (fieldValue: string) => {
        setSelectedFields(selectedFields.filter((f) => f.value !== fieldValue));
    };

    const handleFieldOptionChange = (
        fieldValue: string,
        option: string,
        value: string | string[] | FunctionConfig[],
    ) => {
        setSelectedFields(
            selectedFields.map((field) =>
                field.value === fieldValue
                    ? { ...field, [option]: value }
                    : field,
            ),
        );
    };

    const [editingField, setEditingField] = useState<SourceField | null>(null);
    const [isFieldSettingsOpen, setIsFieldSettingsOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleFieldSettings = (field: SourceField) => {
        setEditingField(field);
        setIsFieldSettingsOpen(true);
    };

    const handleCloseFieldSettings = () => {
        setIsFieldSettingsOpen(false);
        setEditingField(null);
    };

    // Get current field data from selectedFields instead of using editingField snapshot
    const getCurrentField = () => {
        if (!editingField) return null;
        return (
            selectedFields.find(
                (field) => field.value === editingField.value,
            ) || editingField
        );
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            setSelectedFields((items) => {
                const oldIndex = items.findIndex(
                    (item) => item.value === active.id,
                );
                const newIndex = items.findIndex(
                    (item) => item.value === over?.id,
                );

                return arrayMove(items, oldIndex, newIndex);
            });
        }
    };

    // Filter available fields based on search
    const getFilteredFields = () => {
        let filtered = localAvailableSourceFields.filter(
            (field) => !selectedFields.find((f) => f.value === field.value),
        );

        if (searchTerm) {
            filtered = filtered.filter((field) =>
                field.label.toLowerCase().includes(searchTerm.toLowerCase()),
            );
        }

        return filtered;
    };

    const validate = (): ValidationErrors => {
        const nextErrors: ValidationErrors = {};
        const trimmedName = variableName.trim();

        if (!trimmedName) {
            nextErrors.name = "Variable name is required";
        } else if (
            notAvailableFieldNames.includes(trimmedName) &&
            trimmedName !== editedField?.name
        ) {
            nextErrors.name = "Variable name already exists";
        }

        if (mode === "basic") {
            if (selectedFields.length === 0) {
                nextErrors.fields = "Select at least one source field";
            }
        } else if (!advancedPayload.trim()) {
            nextErrors.payload = ["Payload is required"];
        } else {
            const issues = collectPayloadIssues(advancedPayload, allFunctions);
            if (issues.length > 0) nextErrors.payload = issues;
        }

        return nextErrors;
    };

    const handleSubmit = async () => {
        const nextErrors = validate();
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length > 0) return;

        const submitData: {
            name: string;
            fields?: SourceField[];
            payload?: string;
            mode: "basic" | "advanced";
            index: number;
        } =
            mode === "basic"
                ? {
                      name: variableName.trim(),
                      fields: selectedFields,
                      mode: "basic",
                      index: editFieldIndex,
                  }
                : {
                      name: variableName.trim(),
                      payload: advancedPayload,
                      mode: "advanced",
                      index: editFieldIndex,
                  };

        setIsSubmitting(true);
        try {
            const result = await onSubmit(submitData);
            if (result === false) return;

            setVariableName("");
            setSelectedFields([]);
            setAdvancedPayload("");
            setMode("basic");
            setErrors({});
            onClose();
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={isSubmitting ? undefined : onClose}
                maxWidth="md"
                fullWidth
                className="dynamic-value-modal"
            >
                <DialogTitle>
                    <Typography variant="h5" component="h2">
                        {editFieldIndex !== null
                            ? "Edit Dynamic Value"
                            : "Create Dynamic Value"}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack spacing={3}>
                        {/* Description */}
                        <Typography variant="body2" color="text.secondary">
                            Combine source fields with custom text and
                            functions.
                        </Typography>

                        <Tabs
                            value={mode}
                            onChange={(_, newMode) => setMode(newMode)}
                            sx={{
                                minHeight: 36,
                                "& .MuiTabs-indicator": {
                                    backgroundColor: "#ed6c02",
                                },
                                "& .MuiTab-root": {
                                    minHeight: 36,
                                    textTransform: "none",
                                    fontWeight: 600,
                                    "&.Mui-selected": {
                                        color: "#ed6c02",
                                    },
                                },
                            }}
                        >
                            <Tab label="Basic Mode" value="basic" />
                            <Tab label="Advanced Mode" value="advanced" />
                        </Tabs>

                        {/* Variable Name */}
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ mb: 1, fontWeight: 600 }}
                            >
                                Variable Name
                            </Typography>
                            <TextField
                                fullWidth
                                placeholder="e.g., fullAddress"
                                value={variableName}
                                onChange={(e) => {
                                    setVariableName(e.target.value);
                                    setErrors((prev) => ({
                                        ...prev,
                                        name: undefined,
                                    }));
                                }}
                                variant="outlined"
                                size="small"
                                disabled={nameReadOnly}
                                error={!!errors.name}
                                helperText={
                                    errors.name ??
                                    (nameReadOnly
                                        ? "The field name cannot be changed after creation"
                                        : undefined)
                                }
                            />
                        </Box>

                        {mode === "basic" && (
                            <>
                                {/* Concatenation Order */}
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 1, fontWeight: 600 }}
                                    >
                                        Concatenation Order
                                    </Typography>
                                    <Box
                                        sx={{
                                            ...(errors.fields && {
                                                border: "1px solid",
                                                borderColor: "error.main",
                                                borderRadius: 1,
                                                px: 1,
                                            }),
                                        }}
                                    >
                                        {selectedFields.length === 0 ? (
                                            <Typography
                                                variant="body2"
                                                color={
                                                    errors.fields
                                                        ? "error.main"
                                                        : "text.secondary"
                                                }
                                                align="center"
                                                sx={{ py: 4 }}
                                            >
                                                No fields selected. Add fields
                                                from the source list.
                                            </Typography>
                                        ) : (
                                            <DndContext
                                                sensors={sensors}
                                                collisionDetection={
                                                    closestCenter
                                                }
                                                onDragEnd={handleDragEnd}
                                            >
                                                <SortableContext
                                                    items={selectedFields.map(
                                                        (field) => field.value,
                                                    )}
                                                    strategy={
                                                        verticalListSortingStrategy
                                                    }
                                                >
                                                    <Stack spacing={1}>
                                                        {selectedFields.map(
                                                            (field) => (
                                                                <SortableFieldItem
                                                                    key={
                                                                        field.value
                                                                    }
                                                                    field={
                                                                        field
                                                                    }
                                                                    onSettings={
                                                                        handleFieldSettings
                                                                    }
                                                                    onRemove={
                                                                        handleRemoveField
                                                                    }
                                                                />
                                                            ),
                                                        )}
                                                    </Stack>
                                                </SortableContext>
                                            </DndContext>
                                        )}
                                    </Box>
                                    {errors.fields && (
                                        <FormHelperText error>
                                            {errors.fields}
                                        </FormHelperText>
                                    )}
                                </Box>

                                {/* Available Source Fields */}
                                {localAvailableSourceFields.length > 0 && (
                                    <Box>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            sx={{ mb: 2 }}
                                        >
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 600 }}
                                            >
                                                Available Source Fields
                                            </Typography>
                                            <Badge
                                                badgeContent={
                                                    getFilteredFields().length
                                                }
                                                color="primary"
                                            >
                                                <Button
                                                    variant="outlined"
                                                    color="primary"
                                                    size="small"
                                                    startIcon={
                                                        showAvailableFields ? (
                                                            <RemoveIcon />
                                                        ) : (
                                                            <AddIcon />
                                                        )
                                                    }
                                                    onClick={() =>
                                                        setShowAvailableFields(
                                                            !showAvailableFields,
                                                        )
                                                    }
                                                >
                                                    {showAvailableFields
                                                        ? "Hide Fields"
                                                        : "Browse Fields"}
                                                </Button>
                                            </Badge>
                                        </Stack>

                                        {/* Search Bar */}
                                        <TextField
                                            size="small"
                                            placeholder="Search fields..."
                                            value={searchTerm}
                                            onChange={(e) =>
                                                setSearchTerm(e.target.value)
                                            }
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <SearchIcon />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            sx={{ mb: 2 }}
                                            fullWidth
                                        />

                                        {/* Collapsible Field List */}
                                        {showAvailableFields && (
                                            <Paper
                                                variant="outlined"
                                                sx={{
                                                    p: 2,
                                                    overflow: "auto",
                                                    backgroundColor: "#f8f9fa",
                                                }}
                                            >
                                                {getFilteredFields().length ===
                                                0 ? (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        align="center"
                                                        sx={{ py: 2 }}
                                                    >
                                                        {searchTerm
                                                            ? "No fields match your search."
                                                            : "All fields have been selected."}
                                                    </Typography>
                                                ) : (
                                                    <Stack
                                                        direction="row"
                                                        flexWrap="wrap"
                                                        sx={{ gap: 1 }}
                                                    >
                                                        {getFilteredFields().map(
                                                            (field) => (
                                                                <Chip
                                                                    label={
                                                                        field.label
                                                                    }
                                                                    onClick={() =>
                                                                        handleAddField(
                                                                            field,
                                                                        )
                                                                    }
                                                                    icon={
                                                                        <AddIcon />
                                                                    }
                                                                    variant="outlined"
                                                                    clickable
                                                                    size="small"
                                                                    sx={{
                                                                        mb: 1,
                                                                        "&:hover":
                                                                            {
                                                                                backgroundColor:
                                                                                    "#fff3cd",
                                                                                borderColor:
                                                                                    "#fff3cd",
                                                                            },
                                                                    }}
                                                                />
                                                            ),
                                                        )}
                                                    </Stack>
                                                )}
                                            </Paper>
                                        )}
                                    </Box>
                                )}

                                {/* Preview and Global Settings Row */}
                                <Stack spacing={2}>
                                    <Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 1, fontWeight: 600 }}
                                        >
                                            Preview
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={preview}
                                            variant="outlined"
                                            size="small"
                                            placeholder="Preview will appear here..."
                                            disabled
                                        />
                                    </Box>
                                </Stack>
                            </>
                        )}

                        {mode === "advanced" && (
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    Payload
                                </Typography>
                                <AdvancedPayloadInput
                                    value={advancedPayload}
                                    onChange={(nextPayload) => {
                                        setAdvancedPayload(nextPayload);
                                        setErrors((prev) => ({
                                            ...prev,
                                            payload: undefined,
                                        }));
                                    }}
                                    availableSourceFields={
                                        localAvailableSourceFields || []
                                    }
                                    allFunctions={allFunctions}
                                    error={!!errors.payload?.length}
                                    helperText={errors.payload?.map((issue) => (
                                        <Box
                                            key={issue}
                                            component="span"
                                            sx={{ display: "block" }}
                                        >
                                            {issue}
                                        </Box>
                                    ))}
                                />
                            </Box>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "space-between" }}>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <div className="position-relative overflow-hidden z-1">
                        {isSubmitting && (
                            <div className="circle-loader max-height-45">
                                <ClipLoader color={"#000"} size={17} />
                            </div>
                        )}
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {editFieldIndex !== null
                                ? isSubmitting
                                    ? "Saving..."
                                    : "Save Changes"
                                : isSubmitting
                                  ? "Creating..."
                                  : "Create"}
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>

            {/* Field Settings Modal */}
            <Dialog
                open={isFieldSettingsOpen}
                onClose={handleCloseFieldSettings}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Field Settings: {editingField?.label}
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    {getCurrentField() && (
                        <Stack spacing={3} sx={{ mt: 1 }}>
                            {/* Prefix and Suffix */}
                            <Stack direction="row" spacing={2}>
                                <TextField
                                    label="Prefix"
                                    fullWidth
                                    value={getCurrentField()?.prefix || ""}
                                    onChange={(e) =>
                                        handleFieldOptionChange(
                                            getCurrentField()!.value,
                                            "prefix",
                                            e.target.value,
                                        )
                                    }
                                    placeholder="e.g., ("
                                    variant="outlined"
                                />
                                <TextField
                                    label="Suffix"
                                    fullWidth
                                    value={getCurrentField()?.suffix || ""}
                                    onChange={(e) =>
                                        handleFieldOptionChange(
                                            getCurrentField()!.value,
                                            "suffix",
                                            e.target.value,
                                        )
                                    }
                                    placeholder="e.g., )"
                                    variant="outlined"
                                />
                            </Stack>

                            {/* Default Value */}
                            <TextField
                                label="Default Value"
                                fullWidth
                                value={getCurrentField()?.defaultValue || ""}
                                onChange={(e) =>
                                    handleFieldOptionChange(
                                        getCurrentField()!.value,
                                        "defaultValue",
                                        e.target.value,
                                    )
                                }
                                placeholder="Enter default value if field is empty"
                                variant="outlined"
                                helperText="This value will be used if the field is empty"
                            />

                            {/* Functions */}
                            <Box>
                                <Typography
                                    variant="subtitle2"
                                    sx={{ mb: 1, fontWeight: 600 }}
                                >
                                    Functions
                                </Typography>
                                <Stack spacing={2}>
                                    {(getCurrentField()?.fn || []).map(
                                        (func, index) => {
                                            return (
                                                <FunctionConfigItem
                                                    key={index}
                                                    func={func}
                                                    onUpdate={(updatedFunc) => {
                                                        const updatedFunctions =
                                                            [
                                                                ...(getCurrentField()
                                                                    ?.fn || []),
                                                            ];
                                                        updatedFunctions[
                                                            index
                                                        ] = updatedFunc;
                                                        handleFieldOptionChange(
                                                            getCurrentField()!
                                                                .value,
                                                            "fn",
                                                            updatedFunctions,
                                                        );
                                                    }}
                                                    onRemove={() => {
                                                        const updatedFunctions =
                                                            (
                                                                getCurrentField()
                                                                    ?.fn || []
                                                            ).filter(
                                                                (_, i) =>
                                                                    i !== index,
                                                            );
                                                        handleFieldOptionChange(
                                                            getCurrentField()!
                                                                .value,
                                                            "fn",
                                                            updatedFunctions,
                                                        );
                                                    }}
                                                    allFunctions={allFunctions}
                                                />
                                            );
                                        },
                                    )}
                                    <Button
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            const newFunction: FunctionConfig =
                                                {
                                                    fn_name: "",
                                                    fn_values: {},
                                                };
                                            const updatedFunctions = [
                                                ...(getCurrentField()?.fn ||
                                                    []),
                                                newFunction,
                                            ];
                                            handleFieldOptionChange(
                                                getCurrentField()!.value,
                                                "fn",
                                                updatedFunctions,
                                            );
                                        }}
                                        size="small"
                                    >
                                        Add Function
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button
                        variant="outlined"
                        onClick={handleCloseFieldSettings}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCloseFieldSettings}
                        color="primary"
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default CreateDynamicValueModal;
